import { Download, Plug } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { Alert, Button, Card, Checkbox, Field, Input, Select } from '@/components/ui'
import { browserTimezone } from '@/domain/dates'
import { DEFAULT_OFFSETS } from '@/domain/types'
import { useAuth } from '@/lib/auth'
import { canWrite, type ConnectedApp } from '@/lib/mcp'
import { deleteAccount, errorMessage, pb } from '@/lib/pb'
import { useConnectedApps, useDisconnectApp, useUpdateSettings } from '@/lib/queries'

/** A short list beats a 400-entry dropdown; the browser's own zone is added too. */
const COMMON_ZONES = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Belgrade',
  'Europe/Madrid',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Australia/Sydney',
  'UTC',
]

export function Settings() {
  const { user } = useAuth()
  const save = useUpdateSettings()

  const [timezone, setTimezone] = useState(user?.timezone ?? browserTimezone())
  const [digestHour, setDigestHour] = useState(String(user?.digest_hour ?? 9))
  const [offsets, setOffsets] = useState(
    (user?.default_offsets ?? DEFAULT_OFFSETS).join(', '),
  )
  const [emailReminders, setEmailReminders] = useState(user?.email_reminders ?? true)
  const [validation, setValidation] = useState('')
  const [saved, setSaved] = useState(false)

  const zones = COMMON_ZONES.includes(timezone) ? COMMON_ZONES : [timezone, ...COMMON_ZONES]

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    setValidation('')
    setSaved(false)

    const parsed = offsets
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p !== '')
      .map(Number)

    if (parsed.some((n) => !Number.isInteger(n) || n < 0 || n > 3650)) {
      setValidation('Reminder days must be whole numbers between 0 and 3650.')
      return
    }
    if (parsed.length === 0) {
      setValidation('Keep at least one reminder day, or you will never be told anything.')
      return
    }

    const hour = Number(digestHour)
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
      setValidation('Digest hour must be between 0 and 23.')
      return
    }

    save.mutate(
      {
        timezone,
        digest_hour: hour,
        default_offsets: [...new Set(parsed)].sort((a, b) => b - a),
        email_reminders: emailReminders,
      },
      {
        onSuccess: () => {
          setSaved(true)
          // Refresh the auth record so the form reflects what was stored.
          void pb.collection('users').authRefresh()
        },
      },
    )
  }

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-lg font-semibold text-ink">Settings</h1>

      <form onSubmit={onSubmit} className="space-y-4">
        <Card className="space-y-4">
          <div>
            <p className="text-sm font-medium text-ink">Reminder emails</p>
            <p className="text-xs text-ink-muted">
              One email per day at most, grouping everything that needs attention.
            </p>
          </div>

          {save.error && <Alert>{errorMessage(save.error)}</Alert>}
          {validation && <Alert>{validation}</Alert>}
          {saved && <Alert tone="success">Settings saved.</Alert>}

          <Checkbox
            label="Send me reminder emails"
            hint="Turn this off and reminders stop entirely — the dashboard still works."
            checked={emailReminders}
            onChange={(e) => setEmailReminders(e.target.checked)}
          />

          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            <Field label="Timezone" htmlFor="timezone" hint="Decides when your digest is sent.">
              <Select
                id="timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                {zones.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone.replace('_', ' ')}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Send at" htmlFor="hour" hint="Your local time.">
              <Select
                id="hour"
                value={digestHour}
                onChange={(e) => setDigestHour(e.target.value)}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={String(h)}>
                    {String(h).padStart(2, '0')}:00
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field
            label="Remind me this many days ahead"
            htmlFor="offsets"
            hint="Applies to records without their own reminder days. Passport renewals can take weeks, so 90 is a useful first nudge."
          >
            <Input
              id="offsets"
              value={offsets}
              onChange={(e) => setOffsets(e.target.value)}
              placeholder={DEFAULT_OFFSETS.join(', ')}
            />
          </Field>

          <Button type="submit" loading={save.isPending}>
            Save settings
          </Button>
        </Card>
      </form>

      <Card className="space-y-3">
        <div>
          <p className="text-sm font-medium text-ink">Your data</p>
          <p className="text-xs text-ink-muted">
            Every record you have, including costs and notes.
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton format="csv" />
          <ExportButton format="json" />
        </div>
        <p className="text-xs text-ink-muted">
          <Link className="text-brand hover:underline" to="/privacy">
            Privacy
          </Link>
          {' · '}
          <a
            className="text-brand hover:underline"
            href="https://getreminded.app/privacy.html"
            target="_blank"
            rel="noreferrer"
          >
            Full policy
          </a>
        </p>
      </Card>

      <ConnectedAppsCard />

      <Card className="space-y-3">
        <p className="text-sm font-medium text-ink">Account</p>
        <p className="text-xs text-ink-muted">{user?.email}</p>
      </Card>

      <DeleteAccountCard email={user?.email ?? ''} />
    </div>
  )
}

/**
 * Apps connected over MCP, and the way to revoke them.
 *
 * The card is hidden entirely when nothing is connected: most users will never
 * use this, and a permanent empty panel about "MCP" is noise to them. It
 * appears the moment there is something to manage.
 */
function ConnectedAppsCard() {
  const { data: apps, isLoading, error } = useConnectedApps()

  if (isLoading || error || !apps?.length) return null

  return (
    <Card className="space-y-3">
      <div>
        <p className="flex items-center gap-2 text-sm font-medium text-ink">
          <Plug className="size-4 text-brand" aria-hidden="true" />
          Connected apps
        </p>
        <p className="text-xs text-ink-muted">
          AI assistants you have given access to your records. Disconnecting takes effect
          immediately.
        </p>
      </div>
      <ul className="divide-y divide-line">
        {apps.map((app) => (
          <ConnectedAppRow key={app.id} app={app} />
        ))}
      </ul>
    </Card>
  )
}

function ConnectedAppRow({ app }: { app: ConnectedApp }) {
  const disconnect = useDisconnectApp()
  const [confirming, setConfirming] = useState(false)

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm text-ink">{app.client_name || 'Unnamed app'}</p>
        <p className="text-xs text-ink-muted">
          {canWrite(app.scope) ? 'Can read and change records' : 'Can read records only'}
          {' · '}
          {app.last_used ? `last used ${shortDate(app.last_used)}` : 'not used yet'}
        </p>
        {disconnect.error && (
          <p className="mt-1 text-xs text-overdue">{errorMessage(disconnect.error)}</p>
        )}
      </div>
      {confirming ? (
        <div className="flex gap-2">
          <Button
            variant="danger"
            loading={disconnect.isPending}
            onClick={() => disconnect.mutate(app.id)}
          >
            Disconnect
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Keep
          </Button>
        </div>
      ) : (
        <Button variant="ghost" className="text-overdue" onClick={() => setConfirming(true)}>
          Disconnect…
        </Button>
      )}
    </li>
  )
}

/** PocketBase returns "2026-06-01 09:30:00.000Z"; only the date is useful here. */
function shortDate(stamp: string): string {
  const parsed = new Date(stamp.replace(' ', 'T'))
  return Number.isNaN(parsed.getTime()) ? stamp.slice(0, 10) : parsed.toLocaleDateString()
}

function DeleteAccountCard({ email }: { email: string }) {
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function onDelete() {
    if (confirm !== 'DELETE') {
      setError('Type DELETE in capitals to confirm.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await deleteAccount()
      // Auth store cleared; App will route to login.
    } catch (err) {
      setError(errorMessage(err))
      setBusy(false)
    }
  }

  return (
    <Card className="space-y-3 border-overdue/40">
      <div>
        <p className="text-sm font-medium text-ink">Delete account</p>
        <p className="text-xs text-ink-muted">
          Permanently removes your account, all records and reminder history. Export first if you
          want a copy. This cannot be undone.
        </p>
      </div>

      {!open ? (
        <Button type="button" variant="ghost" className="text-overdue" onClick={() => setOpen(true)}>
          Delete my account…
        </Button>
      ) : (
        <div className="space-y-3">
          {error && <Alert>{error}</Alert>}
          <Field
            label="Type DELETE to confirm"
            htmlFor="delete-confirm"
            {...(email ? { hint: `Signed in as ${email}` } : {})}
          >
            <Input
              id="delete-confirm"
              autoComplete="off"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="DELETE"
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="danger"
              loading={busy}
              disabled={confirm !== 'DELETE'}
              onClick={onDelete}
            >
              Delete permanently
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setOpen(false)
                setConfirm('')
                setError('')
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

/**
 * Export needs an authenticated download, and a plain <a href> cannot set the
 * Authorization header — so the file is fetched, then handed to a temporary
 * object-URL link.
 */
function ExportButton({ format }: { format: 'csv' | 'json' }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function download() {
    setBusy(true)
    setError('')
    try {
      const response = await fetch(`/api/export?format=${format}`, {
        headers: { Authorization: pb.authStore.token },
      })
      if (!response.ok) throw new Error(`Export failed (${response.status})`)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `reminded-${new Date().toISOString().slice(0, 10)}.${format}`
      link.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <Button variant="secondary" loading={busy} onClick={download}>
        <Download className="size-4" aria-hidden="true" />
        {format.toUpperCase()}
      </Button>
      {error && <p className="mt-1 text-xs text-overdue">{error}</p>}
    </div>
  )
}
